/**
 * The address field's availability check (plan 3.5, 7.6): a name typed in
 * the Publish sheet is checked against `GET /api/sites/name-availability`,
 * debounced 300 ms, and every newer keystroke cancels the older request, so
 * a slow answer for "sara-bak" can never overwrite the one for "sara-bakery".
 *
 * The static rules (format, reserved and blocked names) come from
 * `@tabbio/utils/site-names` and answer at once without a request; only a
 * well-formed name goes to the server, which also knows taken names, holds
 * and members' usernames. Framework free: the React hook drives it and the
 * tests drive it with fake timers.
 */
import type { SiteNameAvailability, SiteNameUnavailableReason } from '@tabbio/types';
import { checkSiteNamePolicy, normalizeSiteName } from '@tabbio/utils/site-names';

export const SITE_NAME_CHECK_DEBOUNCE_MS = 300;

export type SiteNameCheckState =
  /** Nothing typed yet, or the name equals the site's current address. */
  | { status: 'idle'; name: string }
  | { status: 'checking'; name: string }
  | { status: 'available'; name: string; url: string | null }
  | {
      status: 'unavailable';
      name: string;
      reason: SiteNameUnavailableReason;
      /** The server's words; the sheet shows its own copy for known reasons. */
      message: string;
      suggestions: string[];
    }
  /** The check itself failed (offline, rate limited): publishing re-checks. */
  | { status: 'error'; name: string; message: string };

export type SiteNameFetcher = (
  name: string,
  signal: AbortSignal,
) => Promise<SiteNameAvailability>;

export type SiteNameCheckTimers = {
  setTimeout: (callback: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
};

const DEFAULT_TIMERS: SiteNameCheckTimers = {
  setTimeout: (callback, ms) => setTimeout(callback, ms),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/**
 * What the address input holds, as the user types it: lowercased, spaces
 * become hyphens (a name is one DNS label). Anything else stays, so the
 * format message can say what is wrong.
 */
export function sanitizeSiteNameInput(value: string): string {
  return value.toLowerCase().replace(/\s+/g, '-');
}

/** The answer the static rules give without a request, or null when the server must decide. */
export function localSiteNameVerdict(name: string): SiteNameCheckState | null {
  const normalized = normalizeSiteName(name);
  if (!normalized) return { status: 'idle', name: normalized };
  const policy = checkSiteNamePolicy(normalized);
  if (policy.ok) return null;
  return {
    status: 'unavailable',
    name: policy.normalized,
    reason: policy.reason,
    message: '',
    suggestions: [],
  };
}

export function availabilityToState(answer: SiteNameAvailability): SiteNameCheckState {
  if (answer.available) {
    return { status: 'available', name: answer.normalized, url: answer.url || null };
  }
  return {
    status: 'unavailable',
    name: answer.normalized,
    reason: answer.reason ?? 'invalid',
    message: answer.message,
    suggestions: answer.suggestions.slice(0, 3),
  };
}

function isAbort(error: unknown): boolean {
  return (
    Boolean(error) &&
    typeof error === 'object' &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

/**
 * One address field's checker. `update(name)` for every keystroke; the
 * listener receives each state change. `currentName` (the site's own
 * address when renaming) is idle rather than "taken by you".
 */
export class SiteNameChecker {
  private state: SiteNameCheckState = { status: 'idle', name: '' };
  private timer: unknown = null;
  private controller: AbortController | null = null;
  private generation = 0;
  private disposed = false;

  constructor(
    private readonly fetcher: SiteNameFetcher,
    private readonly listener: (state: SiteNameCheckState) => void,
    private readonly options: {
      debounceMs?: number;
      timers?: SiteNameCheckTimers;
      currentName?: string | null;
      errorMessage?: string;
    } = {},
  ) {}

  read(): SiteNameCheckState {
    return this.state;
  }

  private emit(next: SiteNameCheckState) {
    if (this.disposed) return;
    this.state = next;
    this.listener(next);
  }

  private cancelPending() {
    const timers = this.options.timers ?? DEFAULT_TIMERS;
    if (this.timer !== null) {
      timers.clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  update(raw: string) {
    const name = normalizeSiteName(raw);
    this.generation += 1;
    const generation = this.generation;
    this.cancelPending();
    if (!name || (this.options.currentName && name === this.options.currentName)) {
      this.emit({ status: 'idle', name });
      return;
    }
    const local = localSiteNameVerdict(name);
    if (local) {
      this.emit(local);
      return;
    }
    this.emit({ status: 'checking', name });
    const timers = this.options.timers ?? DEFAULT_TIMERS;
    this.timer = timers.setTimeout(() => {
      this.timer = null;
      const controller = new AbortController();
      this.controller = controller;
      this.fetcher(name, controller.signal).then(
        (answer) => {
          if (generation !== this.generation || controller.signal.aborted) return;
          this.controller = null;
          this.emit(availabilityToState(answer));
        },
        (error: unknown) => {
          if (generation !== this.generation || controller.signal.aborted || isAbort(error)) {
            return;
          }
          this.controller = null;
          this.emit({
            status: 'error',
            name,
            message:
              this.options.errorMessage ??
              (error instanceof Error ? error.message : 'Could not check the name'),
          });
        },
      );
    }, this.options.debounceMs ?? SITE_NAME_CHECK_DEBOUNCE_MS);
  }

  /** Re-check the current name at once (Publish after an error). */
  flush(raw: string) {
    this.update(raw);
  }

  dispose() {
    this.cancelPending();
    this.disposed = true;
  }
}
